import React, { useState } from 'react';
import { Box, IconButton, Popover, Slider } from '@mui/material';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeMute from '@mui/icons-material/VolumeMute';
import VolumeOff from '@mui/icons-material/VolumeOff';
import type { VolumeControlProps } from '../DiscordAudioPlayer.types';
import {
  volumeButtonStyles,
  volumeSliderContainerStyles,
  volumeSliderStyles,
} from '../DiscordAudioPlayer.styles';

/**
 * Get the appropriate volume icon based on volume level and mute state
 */
const getVolumeIcon = (volume: number, isMuted: boolean) => {
  if (isMuted || volume === 0) return <VolumeOff />;
  if (volume < 0.3) return <VolumeMute />;
  if (volume < 0.7) return <VolumeDown />;
  return <VolumeUp />;
};

/**
 * Volume control component with popover slider
 */
export const VolumeControl: React.FC<VolumeControlProps> = ({
  volume,
  isMuted,
  disabled = false,
  onVolumeChange,
  onToggleMute,
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    onVolumeChange(newValue as number);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    const step = event.shiftKey ? 0.1 : 0.05;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        onVolumeChange(Math.min(1, volume + step));
        break;
      case 'ArrowDown':
        event.preventDefault();
        onVolumeChange(Math.max(0, volume - step));
        break;
      case 'm':
      case 'M':
        event.preventDefault();
        onToggleMute();
        break;
    }
  };

  const effectiveVolume = isMuted ? 0 : volume;

  return (
    <>
      <IconButton
        onClick={handleClick}
        onDoubleClick={onToggleMute}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={`Volume: ${Math.round(effectiveVolume * 100)}%${isMuted ? ' (muted)' : ''}`}
        aria-haspopup="dialog"
        sx={[volumeButtonStyles, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      >
        {getVolumeIcon(volume, isMuted)}
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={volumeSliderContainerStyles}>
          <Slider
            value={effectiveVolume}
            onChange={handleSliderChange}
            orientation="vertical"
            min={0}
            max={1}
            step={0.01}
            aria-label="Volume"
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            sx={volumeSliderStyles}
          />
        </Box>
      </Popover>
    </>
  );
};

export default VolumeControl;
